import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const CART_EVENTS = [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate];

/** @param {string} currency @returns {Intl.NumberFormat} */
function createCurrencyFormatter(currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: currency || 'USD',
    });
  } catch {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: 'USD',
    });
  }
}

/**
 * Progressive enhancement for server-rendered cart goals.
 * Horizon remains the only owner of cart mutations; this component only consumes
 * the cart result attached to Shopify standard events.
 */
class AcCartProgress extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number} */
  #pendingUpdates = 0;
  /** @type {number} */
  #latestEventVersion = 0;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#pendingUpdates = 0;
    this.#latestEventVersion = 0;
    this.removeAttribute('aria-busy');
    delete this.dataset.hydrating;
    this.#abortController = new AbortController();

    for (const eventName of CART_EVENTS) {
      document.addEventListener(eventName, this.#handleCartEvent, { signal: this.#abortController.signal });
    }

    this.#update(Number(this.dataset.totalMajor), this.dataset.currency || 'USD');
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#pendingUpdates = 0;
    this.#latestEventVersion += 1;
    this.removeAttribute('aria-busy');
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #handleCartEvent = (event) => {
    if (!event.promise) return;

    const controller = this.#abortController;
    const eventVersion = ++this.#latestEventVersion;
    this.#pendingUpdates += 1;
    this.setAttribute('aria-busy', 'true');

    event.promise
      ?.then(({ cart, detail }) => {
        if (controller?.signal.aborted || !this.isConnected || eventVersion !== this.#latestEventVersion) return;
        if (detail?.didError || !cart?.cost?.totalAmount) {
          this.#setError();
          return;
        }

        const { amount, currencyCode } = cart.cost.totalAmount;
        this.#update(Number(amount), currencyCode || this.dataset.currency || 'USD');
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (controller?.signal.aborted || !this.isConnected || eventVersion !== this.#latestEventVersion) return;
        this.#setError();
        console.warn('[ac-cart-progress] Cart event rejected:', error);
      })
      .finally(() => {
        if (controller?.signal.aborted || !this.isConnected) return;
        this.#pendingUpdates = Math.max(0, this.#pendingUpdates - 1);
        if (this.#pendingUpdates === 0) this.removeAttribute('aria-busy');
      });
  };

  /** @param {number} total @param {string} currency */
  #update(total, currency) {
    if (!Number.isFinite(total)) return;

    this.dataset.totalMajor = String(total);
    delete this.dataset.state;
    const formatter = createCurrencyFormatter(currency);
    const liveStatus = this.querySelector('[data-ac-cart-progress-status]');
    if (liveStatus) liveStatus.textContent = '';

    for (const goal of this.querySelectorAll('[data-ac-cart-goal]')) {
      if (!(goal instanceof HTMLElement)) continue;

      const target = Number(goal.dataset.targetMajor);
      if (!Number.isFinite(target) || target <= 0) continue;

      const reached = total >= target;
      const percent = Math.min(100, Math.max(0, (total / target) * 100));
      const meter = goal.querySelector('[role="progressbar"]');
      const status = goal.querySelector('[data-ac-cart-goal-status]');

      goal.dataset.state = reached ? 'reached' : 'pending';
      goal.style.setProperty('--ac-cart-progress', `${percent}%`);

      if (meter) {
        meter.setAttribute('aria-valuenow', String(Math.round(percent)));
      }

      if (status) {
        const prefix = reached ? goal.dataset.reachedText : goal.dataset.remainingPrefix;
        const value = reached ? '' : formatter.format(Math.max(0, target - total));
        const message = [prefix, value].filter(Boolean).join(' ');
        status.textContent = message;
        meter?.setAttribute('aria-valuetext', message || formatter.format(total));
      }
    }
  }

  #setError() {
    this.dataset.state = 'error';
    const message = this.dataset.errorLabel || '';
    const liveStatus = this.querySelector('[data-ac-cart-progress-status]');
    if (liveStatus) liveStatus.textContent = message;

    for (const meter of this.querySelectorAll('[role="progressbar"]')) {
      meter.setAttribute('aria-valuetext', message);
    }
  }
}

/**
 * Hydrates the optional cart-drawer block region through Horizon's SectionRenderer.
 * `renderSection` owns the full Section Rendering API lifecycle. Hydration mode
 * updates only the keyed cart drawer subtrees, so the dialog and its open/focus
 * state remain owned by Horizon while configured blocks receive fresh markup.
 * https://shopify.dev/docs/api/ajax/section-rendering
 */
class AcCartExtensions extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();

    const sectionId = this.dataset.sectionId;
    if (!sectionId || this.dataset.hydrated === 'true' || this.dataset.hydrating === 'true') return;

    const abortController = new AbortController();
    this.#abortController = abortController;
    this.dataset.hydrating = 'true';

    sectionRenderer
      .renderSection(sectionId, { cache: false, mode: 'hydration', signal: abortController.signal })
      .then(() => {
        if (abortController.signal.aborted || !this.isConnected) return;
        if (!this.matches('[data-hydration-key="ac-cart-extensions"]')) {
          throw new Error('The cart extension region is no longer mounted.');
        }

        this.dataset.hydrated = 'true';
      })
      .catch((error) => {
        if (this.#abortController === abortController) delete this.dataset.hydrated;
        if (error?.name !== 'AbortError') console.warn('[ac-cart-extensions] Unable to hydrate drawer blocks:', error);
      })
      .finally(() => {
        if (this.#abortController !== abortController) return;

        delete this.dataset.hydrating;
        this.#abortController = null;
      });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    delete this.dataset.hydrating;
  }
}

/** Hides a configured cross-sell after its product enters the cart. */
class AcCartCrossSell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartEvent, {
      signal,
    });
    this.querySelector('[data-ac-cross-sell-variant]')?.addEventListener('change', this.#handleVariantChange, {
      signal,
    });

    this.#syncVariantPresentation();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {Event & {promise?: Promise<any>}} event */
  #handleCartEvent = (event) => {
    if (this.dataset.hideWhenInCart !== 'true') return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError || !Array.isArray(detail?.items)) return;
        const productId = String(this.dataset.productId || '');
        const isInCart = detail.items.some((item) => String(item.product_id) === productId);
        this.hidden = isInCart;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ac-cart-cross-sell] Cart event rejected:', error);
      });
  };

  #handleVariantChange = () => {
    this.#syncVariantPresentation();
  };

  #syncVariantPresentation() {
    const select = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('[data-ac-cross-sell-variant]')
    );
    const option = select?.selectedOptions[0];
    if (!option) return;

    const price = option.dataset.price || '';
    const available = option.dataset.available === 'true';
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('button[type="submit"][name="add"]'));

    for (const priceNode of this.querySelectorAll('[data-ac-cross-sell-price], [data-ac-button-price]')) {
      priceNode.textContent = price;
    }
    if (submitButton) submitButton.disabled = !available;
  }
}

if (!customElements.get('ac-cart-progress')) customElements.define('ac-cart-progress', AcCartProgress);
if (!customElements.get('ac-cart-extensions')) customElements.define('ac-cart-extensions', AcCartExtensions);
if (!customElements.get('ac-cart-cross-sell')) customElements.define('ac-cart-cross-sell', AcCartCrossSell);
